import {
  constants,
  getAccessTokenFromLocalStorage,
  getFromLocalStorage,
  setInLocalStorage,
} from 'app/utils/localStorage';

import { useMutation, useQuery } from 'react-query';
import React, {
  createContext,
  FunctionComponent,
  useContext,
  useMemo,
  useState,
} from 'react';
import { getSettings, ISettings } from './state';
import { getAuthorizedModules } from 'auth/Requests';
import { toast } from 'react-toastify';
import { _t } from 'app/utils/i18n';
import {
  firstLevelSideMenuItem,
  getSideMenuState,
  handlelocalStorageArray,
} from './sidemenuState';
import { getSettingsHandler, updateSettingsHandler } from 'settings/Requests';
import { handleUploadImage } from 'app/utils/imageUploader';
import { convertColorsToRGB } from 'app/utils/hex-to-rgb';
import { loadCountriesNationality } from 'app/Requests';

interface ISettingsContext extends ISettings {
  storeSettings: (newSettings: any) => void;
  resetAllSettings: () => void;
}
const SettingsContext = createContext<ISettingsContext | null>(null);

export const SettingsProvider: FunctionComponent<any> = ({
  children,
}): JSX.Element | null => {
  const [settingsState, setSettingsState] = useState(getSettings([]));

  const { data: modules, isError } = useQuery(
    'getAuthorizedModules',
    getAuthorizedModules,
    {
      enabled: !!getAccessTokenFromLocalStorage(),
      onError: () => {
        return false;
      },
      onSuccess: (res) => {
        setInLocalStorage(constants.AUTHORIZED_MODULES, res);
        setSettingsState(getSettings(res));
      },
    }
  );

  const getCountries = useQuery('getCountries', loadCountriesNationality, {
    enabled: false,
  });

  const canParse = (json: string) => {
    try {
      return JSON.parse(json);
    } catch (error) {
      return json;
    }
  };
  const handelTrendingDuration = (item: any) => {
    return { label: _t(`reports:${item.value}`), value: item.value };
  };
  const currentLanguage = getFromLocalStorage(constants.language);

  useQuery(['getSettings'], getSettingsHandler, {
    retry: false,
    enabled: !!modules || isError,
    onSuccess: (data) => {
      const settings = getSettings(modules).theme;
      setSettingsState({
        theme: {
          colors: {
            ...settings.colors,
            secondaryColor: data?.theme_primary_color,
            mainColor: data?.main_text_color,
            chartsColors:
              canParse(data?.chart_colors) || settings.colors.chartsColors,
            darkSecondaryColor: data?.dark_theme_primary_color,
            darkMainColor: data?.dark_main_text_color,
            darkChartsColors:
              canParse(data?.dark_chart_colors) ||
              settings.colors.darkChartsColors,
          },
          app: {
            ...settings.app,

            defaultLanguage: { label: data?.language, value: data?.language },
            logo: data?.image_url,
            faqs: data?.faqs?.[0]?.title
              ? data?.faqs
              : [
                  {
                    title: '',
                    description: '',
                    link_title: '',
                    link: '',
                    title_ar: '',
                    description_ar: '',
                    link_title_ar: '',
                  },
                ],
            identifyTrendingCountries: data?.trending_countries?.map(
              (item: any) => ({ label: item.name, value: item.id })
            ),
            navigations: handlelocalStorageArray(
              canParse(data?.side_menu) || settings.app.navigations,
              getSideMenuState(modules || [])
            ),
            timezone: { label: data?.timezone, value: data?.timezone },
            time_format: data?.time_format,
            date_format: data?.date_format
              ? {
                  label: data?.date_format,
                  value: data?.date_format,
                }
              : settings.app.date_format,
            trending_duration:
              handelTrendingDuration(canParse(data?.trending_duration)) ||
              settings.app.trending_duration,
            title: data?.brand_name,
            defaultCountries: data?.filter_countries?.map((item: any) => ({
              label: item.name,
              value: item.id,
            })),
          },
        },
        darkMode: data?.dark,
      });
      data?.date_format &&
        setInLocalStorage(constants.TIME_FORMAT, data?.date_format);
      if (typeof data?.time_format !== 'undefined')
        setInLocalStorage(constants.TIME_24, data?.time_format);

      canParse(data?.trending_duration) &&
        setInLocalStorage(
          constants.TRENDING_DURATION,
          canParse(data?.trending_duration)?.value
        );

      if (!getFromLocalStorage(constants.language)) {
        setInLocalStorage(constants.language, data?.language);
        location.reload();
      }
    },
    onError: () => {
      const settings = getFromLocalStorage(constants.APP_SETTINGS);
      setSettingsState(
        settings
          ? {
              ...settings,

              theme: {
                ...settings.theme,
                app: {
                  ...settings.theme.app,
                  navigations: handlelocalStorageArray(
                    settings.theme.app.navigations,
                    getSideMenuState(modules || [])
                  ),
                },
              },
            }
          : getSettings(modules)
      );
    },
  });
  const { mutate: updateMutate } = useMutation(updateSettingsHandler, {
    onSuccess: (result) => {
      const records = result?.payload?.records;
      getCountries.refetch();
      toast.success(_t('influencers:edits_done_success'));
      if (records?.language !== currentLanguage) {
        setInLocalStorage(constants.language, records.language);
        window.location.reload();
      }
    },
  });
  const updateTheme = (settings: any) => {
    const data: any = settings.theme;
    setInLocalStorage(constants.TIME_ZONE, data?.app?.timezone?.value);
    setInLocalStorage(constants.TIME_FORMAT, data?.app?.date_format?.value);
    setInLocalStorage(constants.TIME_24, data?.app?.time_format);
    setInLocalStorage(
      constants.TRENDING_DURATION,
      data?.app?.trending_duration?.value
    );

    updateMutate({
      chart_colors: JSON.stringify(data.colors.chartsColors),
      dark_chart_colors: JSON.stringify(data.colors.darkChartsColors),
      brand_name: data.app.title,
      faqs: data.app.faqs,
      filter_countries_ids:
        data?.app?.defaultCountries?.map((item: any) => item?.value) || [],
      image_url: data.app.logoURl,
      language: data?.app?.defaultLanguage?.value,
      main_text_color: data.colors.mainColor,
      dark_main_text_color: data.colors.darkMainColor,
      side_menu: JSON.stringify(data.app.navigations),
      theme_primary_color: data.colors.secondaryColor,
      dark_theme_primary_color: data.colors.darkSecondaryColor,
      timezone: data?.app?.timezone?.value,
      trending_countries_ids:
        data?.app?.identifyTrendingCountries?.map((item: any) => item?.value) ||
        [],
      date_format: data?.app?.date_format?.value,
      time_format: data?.app?.time_format,
      trending_duration: JSON.stringify(data?.app?.trending_duration),
      reset: data?.reset,
    });
  };
  const storeSettings = async (newSettings: any) => {
    const img = await handleUploadImage(newSettings, 'logo');

    const newSettingsState: any = {
      theme: {
        app: {
          defaultLanguage: newSettings?.defaultLanguage,
          faqs: newSettings?.faqs,
          faq_en: newSettings?.faq_en,
          faq_ar: newSettings?.faq_ar,
          identifyTrendingCountries: newSettings?.identifyTrendingCountries,
          logo: newSettings?.logo,
          logoURl: img || newSettings?.logo,
          timezone: newSettings?.timezone,
          title: newSettings?.brandName,
          navigations: newSettings?.navigations,
          defaultCountries: newSettings?.defaultCountries,
          date_format: newSettings?.date_format,
          time_format: newSettings?.time_format,
          trending_duration: newSettings?.trending_duration,
        },
        colors: {
          ...settingsState.theme.colors,
          mainColor: newSettings.textColors[0].color,
          secondaryColor: newSettings.textColors[1].color,
          chartsColors: newSettings.chartsColors.map((item: any) => item.color),
          darkMainColor: newSettings.darkTextColors[0].color,
          darkSecondaryColor: newSettings.darkTextColors[1].color,
          darkChartsColors: newSettings.darkChartsColors.map(
            (item: any) => item.color
          ),
        },
      },
    };
    setSettingsState(newSettingsState);

    const newState = {
      ...newSettingsState,

      theme: {
        ...newSettingsState.theme,
        reset: false,
        app: {
          ...newSettingsState.theme.app,
          navigations: newSettings?.navigations.map(
            (item: firstLevelSideMenuItem) => ({
              id: item.id,
              title: item.title,
              order: item.order,
              menuItems: item.menuItems,
              newTitle: item.newTitle,
            })
          ),
        },
      },
    };
    setInLocalStorage(constants.APP_SETTINGS, newState);
    updateTheme(newState);
  };

  const resetAllSettings = () => {
    const oldSettings = getSettings(modules);
    setSettingsState({
      ...oldSettings,
      theme: {
        ...oldSettings.theme,
        app: {
          ...oldSettings.theme.app,
          navigations: handlelocalStorageArray(
            oldSettings.theme.app.navigations,
            getSideMenuState(modules || [])
          ),
        },
      },
    });

    setInLocalStorage(
      constants.APP_SETTINGS,

      {
        ...oldSettings,

        theme: {
          ...oldSettings.theme,
          app: {
            ...oldSettings.theme.app,
            navigations: oldSettings.theme.app?.navigations.map(
              (item: firstLevelSideMenuItem) => ({
                id: item.id,
                title: item.title,
                order: item.order,
                menuItems: item.menuItems,
              })
            ),
          },
        },
      }
    );
    updateTheme({
      ...oldSettings,

      theme: {
        ...oldSettings.theme,
        reset: true,
        app: {
          ...oldSettings.theme.app,
          logoURl: '',
          navigations: oldSettings.theme.app?.navigations.map(
            (item: firstLevelSideMenuItem) => ({
              id: item.id,
              title: item.title,
              order: item.order,
              menuItems: item.menuItems,
            })
          ),
        },
      },
    });

    toast.success(_t('settings:reset_successfully'));

    return oldSettings;
  };

  const value = useMemo(
    () => ({
      theme: {
        ...settingsState.theme,
        colors: convertColorsToRGB(settingsState.theme.colors),
      },
      storeSettings,
      resetAllSettings,
    }),

    [settingsState]
  );

  return (
    <SettingsContext.Provider value={value as any}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useAppSettings = (): ISettingsContext => {
  const context = useContext(SettingsContext);
  if (!context)
    throw Error('useAppSettings should be used within <SettingProvider />');
  return context;
};
